import { useEffect, useMemo, useRef, useState } from "react";
import { clsx } from "clsx";
import { EMOJI_CATEGORIES } from "./emoji-data";
import { loadEmojiIndex, searchEmoji } from "./emoji-search";

interface EmojiPickerProps {
  onPick: (emoji: string) => void;
  onClose: () => void;
}

type EmojiIndex = Awaited<ReturnType<typeof loadEmojiIndex>>;

export function EmojiPicker({ onPick, onClose }: EmojiPickerProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [activeKey, setActiveKey] = useState(EMOJI_CATEGORIES[0].key);
  const [search, setSearch] = useState("");
  const [index, setIndex] = useState<EmojiIndex | null>(null);
  const [indexError, setIndexError] = useState(false);

  const query = search.trim();

  // Fetch the keyword metadata on the first keystroke only.
  useEffect(() => {
    if (!query || index) return;
    let cancelled = false;
    loadEmojiIndex()
      .then((loaded) => {
        if (!cancelled) setIndex(loaded);
      })
      .catch(() => {
        if (!cancelled) setIndexError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [query, index]);

  const visible = useMemo(() => {
    if (!query) {
      const cat = EMOJI_CATEGORIES.find((c) => c.key === activeKey);
      return cat?.emojis ?? [];
    }
    if (!index) return [];
    return searchEmoji(query, index);
  }, [activeKey, query, index]);

  const searching = Boolean(query);
  const loading = searching && !index && !indexError;

  return (
    <div
      ref={ref}
      className="bg-white border border-gray-200 rounded-md shadow-xl w-80 h-96 flex flex-col overflow-hidden"
    >
      <div className="flex border-b border-gray-200 bg-gray-50">
        {EMOJI_CATEGORIES.map((cat) => (
          <button
            key={cat.key}
            type="button"
            onClick={() => {
              setActiveKey(cat.key);
              setSearch("");
            }}
            title={cat.label}
            className={clsx(
              "flex-1 py-1.5 text-base hover:bg-amber-50",
              activeKey === cat.key && !searching ? "bg-amber-100" : "",
            )}
          >
            {cat.icon}
          </button>
        ))}
      </div>
      <input
        type="text"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Szukaj…"
        aria-label="Szukaj emotki"
        className="border-b border-gray-200 px-2 py-1 text-sm focus:outline-none"
      />
      <div className="flex-1 overflow-y-auto p-1">
        {loading ? (
          <p className="p-2 text-xs text-gray-500">Ładowanie…</p>
        ) : searching && indexError ? (
          <p className="p-2 text-xs text-red-600">
            Nie udało się wczytać wyszukiwarki emotek.
          </p>
        ) : searching && visible.length === 0 ? (
          <p className="p-2 text-xs text-gray-500">Brak wyników.</p>
        ) : (
          <div className="grid grid-cols-8 gap-0.5" data-testid="emoji-grid">
            {visible.map((e, i) => (
              <button
                key={`${e}-${i}`}
                type="button"
                onClick={() => {
                  onPick(e);
                  onClose();
                }}
                className="text-lg leading-none p-1.5 hover:bg-amber-50 rounded"
              >
                {e}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
